import { useState, useEffect, useCallback } from 'react';
import type { ExamApiRecord as Exam } from '../../services/examApi';
import { fetchClasses, fetchClassSections, fetchClassSubjects, type ClassApiRecord, type SectionApiRecord } from '../../services/classApi';
import { fetchStaffOptions, type StaffOption } from '../../services/schoolApi';
import { createExamSchedule, updateExamSchedule, fetchExamSchedules as fetchExamSchedulesApi, type ExamScheduleApiRecord, type SchedulePayload } from '../../services/examApi';
import type { ApiError } from '../../services/api';
import './ExamMaster.css';

// ── Helpers ─────────────────────────────────────────────────

function formatDate(iso: string): string {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const EXAM_TYPE_LABELS: Record<string, string> = {
    periodic: 'Periodic',
    midterm: 'Mid-Term',
    final: 'Final',
    supplementary: 'Supplementary',
};

// ── Types ───────────────────────────────────────────────────

type ScheduleRow = {
    /** Class-subject allocation id — used as React key */
    key: string;
    /** school_subjects.id — the value sent to the API as subject_id */
    subject_id: string;
    subject_name: string;
    subject_code: string;
    /** Existing schedule id if editing, null if new */
    schedule_id: string | null;
    exam_date: string;
    start_time: string;
    end_time: string;
    room_number: string;
    max_marks: string;
    pass_marks: string;
    /** users.id of the invigilator */
    invigilator_id: string;
    dirty: boolean;
    saving: boolean;
    saved: boolean;
    errors: Record<string, string>;
};

type Props = {
    exam: Exam;
    onBack: () => void;
    onSuccess: (msg: string) => void;
};

// ── Component ───────────────────────────────────────────────

export default function ExamScheduleEditor({ exam, onBack, onSuccess }: Props) {
    // Filter state
    const [classes, setClasses] = useState<ClassApiRecord[]>([]);
    const [sections, setSections] = useState<SectionApiRecord[]>([]);
    const [staff, setStaff] = useState<StaffOption[]>([]);
    const [selectedClassId, setSelectedClassId] = useState('');
    const [selectedSectionId, setSelectedSectionId] = useState('');

    // Subject rows (the editable grid)
    const [rows, setRows] = useState<ScheduleRow[]>([]);
    const [existingSchedules, setExistingSchedules] = useState<ExamScheduleApiRecord[]>([]);

    // Loading states
    const [initialLoading, setInitialLoading] = useState(true);
    const [subjectsLoading, setSubjectsLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    // ── Load initial data ───────────────────────────────────

    useEffect(() => {
        (async () => {
            try {
                const [classesRes, staffRes, schedulesRes] = await Promise.all([
                    fetchClasses({ perPage: 100 }),
                    fetchStaffOptions(),
                    fetchExamSchedulesApi(exam.id, { perPage: 200 }),
                ]);
                setClasses(classesRes.data);
                setStaff(staffRes);
                setExistingSchedules(schedulesRes.data);
            } catch {
                setErrorMsg('Failed to load data. Please try again.');
            } finally {
                setInitialLoading(false);
            }
        })();
    }, [exam.id]);

    // ── Cascade: load sections when class changes ───────────

    useEffect(() => {
        if (!selectedClassId) { setSections([]); return; }
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchClassSections(selectedClassId, { perPage: 100 });
                if (!cancelled) setSections(res.data);
            } catch {
                if (!cancelled) setSections([]);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedClassId]);

    // ── Load subjects when class (+section) is selected ─────

    const loadSubjects = useCallback(async () => {
        if (!selectedClassId) { setRows([]); return; }
        setSubjectsLoading(true);
        setErrorMsg('');
        try {
            const res = await fetchClassSubjects(selectedClassId, {
                perPage: 100,
                sectionId: selectedSectionId || undefined,
            });

            const newRows: ScheduleRow[] = [];
            for (const cs of res.data) {
                const subjectName = cs.subject_name || cs.board_subject?.name || '—';
                const subjectCode = cs.subject_code || cs.board_subject?.code || '';

                // Find existing schedule matching this subject + class + section
                const existing = existingSchedules.find(
                    (s) => s.school_subject_id === cs.school_subject_id &&
                        s.class_id === selectedClassId &&
                        (selectedSectionId ? s.section_id === selectedSectionId : !s.section_id)
                );

                newRows.push({
                    key: cs.id,
                    subject_id: cs.school_subject_id,
                    subject_name: subjectName,
                    subject_code: subjectCode,
                    schedule_id: existing?.id ?? null,
                    exam_date: existing?.exam_date ?? exam.start_date,
                    start_time: existing?.start_time?.substring(0, 5) ?? '',
                    end_time: existing?.end_time?.substring(0, 5) ?? '',
                    room_number: existing?.room_number ?? '',
                    max_marks: existing ? String(existing.max_marks) : String(cs.board_subject?.max_marks ?? 100),
                    pass_marks: existing ? String(existing.pass_marks) : '33',
                    invigilator_id: existing?.invigilator_id ?? '',
                    dirty: false,
                    saving: false,
                    saved: !!existing,
                    errors: {},
                });
            }

            setRows(newRows);
        } catch {
            setRows([]);
            setErrorMsg('Failed to load subjects for this class.');
        } finally {
            setSubjectsLoading(false);
        }
    }, [selectedClassId, selectedSectionId, existingSchedules, exam.start_date]);

    useEffect(() => {
        loadSubjects();
    }, [loadSubjects]);

    // ── Update a single row field ───────────────────────────

    const updateRow = (key: string, field: keyof ScheduleRow, value: string) => {
        setRows((prev) => prev.map((r) => {
            if (r.key !== key) return r;
            const updated = { ...r, [field]: value, dirty: true };
            // Clear field error on change
            if (updated.errors[field]) {
                updated.errors = { ...updated.errors };
                delete updated.errors[field];
            }
            return updated;
        }));
    };

    // ── Apply value to all rows ─────────────────────────────

    const applyToAll = (field: 'exam_date' | 'start_time' | 'end_time' | 'room_number' | 'max_marks' | 'pass_marks' | 'invigilator_id', value: string) => {
        setRows((prev) => prev.map((r) => ({ ...r, [field]: value, dirty: true })));
    };

    // ── Validate rows (client-side, mirrors Laravel rules) ──

    const validateRows = (): boolean => {
        let valid = true;
        setRows((prev) => prev.map((r) => {
            // Only validate dirty rows
            if (!r.dirty) return r;

            const errors: Record<string, string> = {};

            // exam_date: required, date
            if (!r.exam_date) errors.exam_date = 'Required';

            // start_time: required, date_format:H:i
            if (!r.start_time) errors.start_time = 'Required';

            // end_time: required, date_format:H:i, after:start_time
            if (!r.end_time) errors.end_time = 'Required';
            else if (r.start_time && r.end_time <= r.start_time) errors.end_time = 'Must be after start';

            // max_marks: sometimes, numeric, min:0
            if (r.max_marks !== '' && (isNaN(Number(r.max_marks)) || Number(r.max_marks) < 0)) {
                errors.max_marks = 'Must be >= 0';
            }

            // pass_marks: sometimes, numeric, min:0, lte:max_marks
            if (r.pass_marks !== '') {
                const passM = Number(r.pass_marks);
                const maxM = Number(r.max_marks);
                if (isNaN(passM) || passM < 0) {
                    errors.pass_marks = 'Must be >= 0';
                } else if (r.max_marks !== '' && !isNaN(maxM) && passM > maxM) {
                    errors.pass_marks = 'Exceeds max marks';
                }
            }

            if (Object.keys(errors).length > 0) valid = false;
            return { ...r, errors };
        }));
        return valid;
    };

    // ── Build payload matching Laravel validation ────────────

    const buildPayload = (row: ScheduleRow): SchedulePayload => {
        const payload: SchedulePayload = {
            school_id: exam.school_id,
            class_id: selectedClassId,
            school_subject_id: row.subject_id,
            exam_date: row.exam_date,
            start_time: row.start_time,
            end_time: row.end_time,
        };

        // section_id: nullable uuid
        if (selectedSectionId) {
            payload.section_id = selectedSectionId;
        } else {
            payload.section_id = null;
        }

        // room_number: nullable string max:50
        if (row.room_number.trim()) {
            payload.room_number = row.room_number.trim();
        } else {
            payload.room_number = null;
        }

        // max_marks: sometimes numeric min:0
        if (row.max_marks !== '') {
            payload.max_marks = Number(row.max_marks);
        }

        // pass_marks: sometimes numeric min:0 lte:max_marks
        if (row.pass_marks !== '') {
            payload.pass_marks = Number(row.pass_marks);
        }

        // invigilator_id: nullable uuid (users.id)
        if (row.invigilator_id) {
            payload.invigilator_id = row.invigilator_id;
        } else {
            payload.invigilator_id = null;
        }

        return payload;
    };

    // ── Save all dirty rows ─────────────────────────────────

    const handleSaveAll = async () => {
        const dirtyRows = rows.filter((r) => r.dirty);
        if (dirtyRows.length === 0) {
            setErrorMsg('No changes to save.');
            return;
        }

        if (!validateRows()) return;

        setSaving(true);
        setErrorMsg('');

        // Mark all dirty rows as saving
        setRows((prev) => prev.map((r) => r.dirty ? { ...r, saving: true, errors: {} } : r));

        let savedCount = 0;
        let failedCount = 0;
        const updatedSchedules: ExamScheduleApiRecord[] = [];

        // Send each row sequentially to avoid overwhelming the server
        for (const row of dirtyRows) {
            const payload = buildPayload(row);
            try {
                let result: ExamScheduleApiRecord;
                if (row.schedule_id) {
                    // Update existing schedule
                    result = await updateExamSchedule(exam.id, row.schedule_id, payload);
                } else {
                    // Create new schedule
                    result = await createExamSchedule(exam.id, payload);
                }
                updatedSchedules.push(result);

                // Mark row as saved
                setRows((prev) => prev.map((r) =>
                    r.key === row.key
                        ? { ...r, schedule_id: result.id, dirty: false, saving: false, saved: true, errors: {} }
                        : r
                ));
                savedCount++;
            } catch (err) {
                const apiErr = err as ApiError;
                // Map Laravel validation errors to the row
                const rowErrors: Record<string, string> = {};
                if (apiErr.errors) {
                    for (const [field, messages] of Object.entries(apiErr.errors)) {
                        rowErrors[field] = messages[0];
                    }
                } else {
                    rowErrors._general = apiErr.message || 'Failed to save';
                }

                setRows((prev) => prev.map((r) =>
                    r.key === row.key
                        ? { ...r, saving: false, errors: rowErrors }
                        : r
                ));
                failedCount++;
            }
        }

        // Update existing schedules cache
        if (updatedSchedules.length > 0) {
            setExistingSchedules((prev) => {
                const updatedIds = new Set(updatedSchedules.map((s) => s.id));
                const others = prev.filter((s) => !updatedIds.has(s.id));
                return [...others, ...updatedSchedules];
            });
        }

        setSaving(false);

        // Show result message
        if (failedCount === 0) {
            const msg = `${savedCount} schedule${savedCount > 1 ? 's' : ''} saved successfully!`;
            setSuccessMsg(msg);
            onSuccess(msg);
            setTimeout(() => setSuccessMsg(''), 5000);
        } else if (savedCount > 0) {
            setErrorMsg(`${savedCount} saved, ${failedCount} failed. Fix the errors and retry.`);
        } else {
            setErrorMsg(`Failed to save schedules. Please check the errors below.`);
        }
    };

    // ── Counts ──────────────────────────────────────────────

    const dirtyCount = rows.filter((r) => r.dirty).length;
    const hasErrors = rows.some((r) => Object.keys(r.errors).length > 0);

    // ── Render ──────────────────────────────────────────────

    return (
        <div className="exams">
            {/* Toasts */}
            {successMsg && (
                <div className="exams__toast">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    <span>{successMsg}</span>
                    <button className="exams__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}
            {errorMsg && (
                <div className="exams__toast exams__toast--error">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                    {errorMsg}
                    <button className="exams__toast-dismiss" onClick={() => setErrorMsg('')}>&times;</button>
                </div>
            )}

            {/* Header */}
            <div className="exams__header">
                <div className="exams__header-text" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button className="exams__back-btn" onClick={onBack} title="Back to exams">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
                    </button>
                    <div>
                        <h1 className="exams__title">Schedule Editor</h1>
                        <p className="exams__subtitle">
                            {exam.name}
                            <span className={`exams__badge exams__badge--${exam.exam_type}`} style={{ marginLeft: 8 }}>{EXAM_TYPE_LABELS[exam.exam_type]}</span>
                            <span className="sch-editor__date-range">{formatDate(exam.start_date)} – {formatDate(exam.end_date)}</span>
                        </p>
                    </div>
                </div>
            </div>

            {/* Filter bar */}
            <div className="sch-editor__filters">
                <div className="sch-editor__filter-group">
                    <div className="sch-editor__filter">
                        <label className="exams__label exams__label--required" htmlFor="schEdClass">Class</label>
                        <select
                            className="exams__input exams__select"
                            id="schEdClass"
                            value={selectedClassId}
                            disabled={initialLoading}
                            onChange={(e) => { setSelectedClassId(e.target.value); setSelectedSectionId(''); }}
                        >
                            <option value="">Select class…</option>
                            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="sch-editor__filter">
                        <label className="exams__label" htmlFor="schEdSection">Section</label>
                        <select
                            className="exams__input exams__select"
                            id="schEdSection"
                            value={selectedSectionId}
                            disabled={!selectedClassId || sections.length === 0}
                            onChange={(e) => setSelectedSectionId(e.target.value)}
                        >
                            <option value="">All sections</option>
                            {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    </div>
                </div>
                {selectedClassId && rows.length > 0 && (
                    <div className="sch-editor__filter-info">
                        <span className="sch-editor__subject-count">{rows.length} subject{rows.length !== 1 ? 's' : ''}</span>
                        {dirtyCount > 0 && <span className="sch-editor__dirty-count">{dirtyCount} to save</span>}
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="exams__table-wrap">
                {initialLoading ? (
                    <div className="exams__loading"><div className="exams__loader"><span className="exams__loader-dot" /><span className="exams__loader-dot" /><span className="exams__loader-dot" /></div></div>
                ) : !selectedClassId ? (
                    <div className="exams__empty">
                        <div className="exams__empty-icon"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg></div>
                        <h3 className="exams__empty-title">Select a class to begin</h3>
                        <p className="exams__empty-text">Choose a class above to load its subjects and configure the exam schedule.</p>
                    </div>
                ) : subjectsLoading ? (
                    <div className="exams__loading"><div className="exams__loader"><span className="exams__loader-dot" /><span className="exams__loader-dot" /><span className="exams__loader-dot" /></div></div>
                ) : rows.length === 0 ? (
                    <div className="exams__empty">
                        <div className="exams__empty-icon"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" /></svg></div>
                        <h3 className="exams__empty-title">No subjects found</h3>
                        <p className="exams__empty-text">No subjects are allocated to this class/section combination.</p>
                    </div>
                ) : (
                    <>
                        {/* Quick-fill bar */}
                        <QuickFillBar
                            exam={exam}
                            staff={staff}
                            onApply={applyToAll}
                        />

                        {/* Editable grid */}
                        <div className="sch-editor__grid-wrap">
                            <table className="sch-editor__table">
                                <thead>
                                    <tr>
                                        <th className="sch-editor__th sch-editor__th--subject">Subject</th>
                                        <th className="sch-editor__th">Exam Date</th>
                                        <th className="sch-editor__th">Start Time</th>
                                        <th className="sch-editor__th">End Time</th>
                                        <th className="sch-editor__th">Room / Hall</th>
                                        <th className="sch-editor__th sch-editor__th--narrow">Max Marks</th>
                                        <th className="sch-editor__th sch-editor__th--narrow">Pass Marks</th>
                                        <th className="sch-editor__th">Invigilator</th>
                                        <th className="sch-editor__th sch-editor__th--status" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <ScheduleRowEditor
                                            key={row.key}
                                            row={row}
                                            exam={exam}
                                            staff={staff}
                                            onChange={(field, value) => updateRow(row.key, field, value)}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            {/* Save footer */}
            {rows.length > 0 && selectedClassId && (
                <div className="sch-editor__footer">
                    <div className="sch-editor__footer-info">
                        {hasErrors && <span className="sch-editor__footer-error">Please fix the highlighted errors.</span>}
                    </div>
                    <div className="sch-editor__footer-actions">
                        <button className="exams__cancel" onClick={onBack} disabled={saving}>Cancel</button>
                        <button className="exams__submit" disabled={saving || dirtyCount === 0} onClick={handleSaveAll}>
                            {saving ? (
                                <>
                                    <svg className="exams__spinner" width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" /></svg>
                                    Saving…
                                </>
                            ) : (
                                <>Save {dirtyCount > 0 ? `${dirtyCount} Schedule${dirtyCount > 1 ? 's' : ''}` : 'Schedules'}</>
                            )}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── QuickFillBar ────────────────────────────────────────────

type QuickFillBarProps = {
    exam: Exam;
    staff: StaffOption[];
    onApply: (field: 'exam_date' | 'start_time' | 'end_time' | 'room_number' | 'max_marks' | 'pass_marks' | 'invigilator_id', value: string) => void;
};

function QuickFillBar({ exam, staff, onApply }: QuickFillBarProps) {
    const [qfDate, setQfDate] = useState(exam.start_date);
    const [qfStart, setQfStart] = useState('');
    const [qfEnd, setQfEnd] = useState('');
    const [qfRoom, setQfRoom] = useState('');
    const [qfMax, setQfMax] = useState('');
    const [qfPass, setQfPass] = useState('');
    const [qfInvigilator, setQfInvigilator] = useState('');

    return (
        <div className="sch-editor__quickfill">
            <div className="sch-editor__quickfill-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M2 12h20" /></svg>
                Apply to all rows
            </div>
            <div className="sch-editor__quickfill-fields">
                <div className="sch-editor__qf-group">
                    <input className="sch-editor__qf-input" type="date" value={qfDate} min={exam.start_date} max={exam.end_date} onChange={(e) => setQfDate(e.target.value)} title="Exam date" />
                    <button className="sch-editor__qf-apply" onClick={() => onApply('exam_date', qfDate)} disabled={!qfDate}>Apply</button>
                </div>
                <div className="sch-editor__qf-group">
                    <input className="sch-editor__qf-input" type="time" value={qfStart} onChange={(e) => setQfStart(e.target.value)} placeholder="Start" title="Start time" />
                    <button className="sch-editor__qf-apply" onClick={() => onApply('start_time', qfStart)} disabled={!qfStart}>Apply</button>
                </div>
                <div className="sch-editor__qf-group">
                    <input className="sch-editor__qf-input" type="time" value={qfEnd} onChange={(e) => setQfEnd(e.target.value)} placeholder="End" title="End time" />
                    <button className="sch-editor__qf-apply" onClick={() => onApply('end_time', qfEnd)} disabled={!qfEnd}>Apply</button>
                </div>
                <div className="sch-editor__qf-group">
                    <input className="sch-editor__qf-input" type="text" value={qfRoom} onChange={(e) => setQfRoom(e.target.value)} placeholder="Room" title="Room / Hall" maxLength={50} />
                    <button className="sch-editor__qf-apply" onClick={() => onApply('room_number', qfRoom)} disabled={!qfRoom}>Apply</button>
                </div>
                <div className="sch-editor__qf-group">
                    <input className="sch-editor__qf-input sch-editor__qf-input--narrow" type="number" value={qfMax} onChange={(e) => setQfMax(e.target.value)} placeholder="Max" title="Max marks" min="0" />
                    <button className="sch-editor__qf-apply" onClick={() => onApply('max_marks', qfMax)} disabled={!qfMax}>Apply</button>
                </div>
                <div className="sch-editor__qf-group">
                    <input className="sch-editor__qf-input sch-editor__qf-input--narrow" type="number" value={qfPass} onChange={(e) => setQfPass(e.target.value)} placeholder="Pass" title="Pass marks" min="0" />
                    <button className="sch-editor__qf-apply" onClick={() => onApply('pass_marks', qfPass)} disabled={!qfPass}>Apply</button>
                </div>
                <div className="sch-editor__qf-group">
                    <select className="sch-editor__qf-input" value={qfInvigilator} onChange={(e) => setQfInvigilator(e.target.value)} title="Invigilator">
                        <option value="">Invigilator…</option>
                        {staff.map((s) => <option key={s.userId} value={s.userId}>{s.name}</option>)}
                    </select>
                    <button className="sch-editor__qf-apply" onClick={() => onApply('invigilator_id', qfInvigilator)} disabled={!qfInvigilator}>Apply</button>
                </div>
            </div>
        </div>
    );
}

// ── ScheduleRowEditor ───────────────────────────────────────

type ScheduleRowEditorProps = {
    row: ScheduleRow;
    exam: Exam;
    staff: StaffOption[];
    onChange: (field: keyof ScheduleRow, value: string) => void;
};

function ScheduleRowEditor({ row, exam, staff, onChange }: ScheduleRowEditorProps) {
    const hasErrors = Object.keys(row.errors).length > 0;

    return (
        <tr className={`sch-editor__row ${hasErrors ? 'sch-editor__row--error' : ''} ${row.dirty ? 'sch-editor__row--dirty' : ''} ${row.saving ? 'sch-editor__row--saving' : ''}`}>
            <td className="sch-editor__td sch-editor__td--subject">
                <div className="sch-editor__subject-name">{row.subject_name}</div>
                {row.subject_code && <div className="sch-editor__subject-code">{row.subject_code}</div>}
                {row.errors._general && <div className="sch-editor__cell-error">{row.errors._general}</div>}
            </td>
            <td className="sch-editor__td">
                <input
                    className={`sch-editor__cell-input ${row.errors.exam_date ? 'sch-editor__cell-input--error' : ''}`}
                    type="date"
                    value={row.exam_date}
                    min={exam.start_date}
                    max={exam.end_date}
                    disabled={row.saving}
                    onChange={(e) => onChange('exam_date', e.target.value)}
                />
                {row.errors.exam_date && <span className="sch-editor__cell-error">{row.errors.exam_date}</span>}
            </td>
            <td className="sch-editor__td">
                <input
                    className={`sch-editor__cell-input ${row.errors.start_time ? 'sch-editor__cell-input--error' : ''}`}
                    type="time"
                    value={row.start_time}
                    disabled={row.saving}
                    onChange={(e) => onChange('start_time', e.target.value)}
                />
                {row.errors.start_time && <span className="sch-editor__cell-error">{row.errors.start_time}</span>}
            </td>
            <td className="sch-editor__td">
                <input
                    className={`sch-editor__cell-input ${row.errors.end_time ? 'sch-editor__cell-input--error' : ''}`}
                    type="time"
                    value={row.end_time}
                    disabled={row.saving}
                    onChange={(e) => onChange('end_time', e.target.value)}
                />
                {row.errors.end_time && <span className="sch-editor__cell-error">{row.errors.end_time}</span>}
            </td>
            <td className="sch-editor__td">
                <input
                    className={`sch-editor__cell-input ${row.errors.room_number ? 'sch-editor__cell-input--error' : ''}`}
                    type="text"
                    value={row.room_number}
                    placeholder="—"
                    maxLength={50}
                    disabled={row.saving}
                    onChange={(e) => onChange('room_number', e.target.value)}
                />
                {row.errors.room_number && <span className="sch-editor__cell-error">{row.errors.room_number}</span>}
            </td>
            <td className="sch-editor__td">
                <input
                    className={`sch-editor__cell-input sch-editor__cell-input--narrow ${row.errors.max_marks ? 'sch-editor__cell-input--error' : ''}`}
                    type="number"
                    value={row.max_marks}
                    min="0"
                    step="any"
                    disabled={row.saving}
                    onChange={(e) => onChange('max_marks', e.target.value)}
                />
                {row.errors.max_marks && <span className="sch-editor__cell-error">{row.errors.max_marks}</span>}
            </td>
            <td className="sch-editor__td">
                <input
                    className={`sch-editor__cell-input sch-editor__cell-input--narrow ${row.errors.pass_marks ? 'sch-editor__cell-input--error' : ''}`}
                    type="number"
                    value={row.pass_marks}
                    min="0"
                    step="any"
                    disabled={row.saving}
                    onChange={(e) => onChange('pass_marks', e.target.value)}
                />
                {row.errors.pass_marks && <span className="sch-editor__cell-error">{row.errors.pass_marks}</span>}
            </td>
            <td className="sch-editor__td">
                <select
                    className={`sch-editor__cell-input sch-editor__cell-select ${row.errors.invigilator_id ? 'sch-editor__cell-input--error' : ''}`}
                    value={row.invigilator_id}
                    disabled={row.saving}
                    onChange={(e) => onChange('invigilator_id', e.target.value)}
                >
                    <option value="">—</option>
                    {staff.map((s) => <option key={s.userId} value={s.userId}>{s.name}</option>)}
                </select>
                {row.errors.invigilator_id && <span className="sch-editor__cell-error">{row.errors.invigilator_id}</span>}
            </td>
            <td className="sch-editor__td sch-editor__td--status">
                {row.saving ? (
                    <svg className="exams__spinner" width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" /></svg>
                ) : row.saved && !row.dirty ? (
                    <span className="sch-editor__status-saved" title="Saved">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                    </span>
                ) : row.dirty ? (
                    <span className="sch-editor__status-new" title="Unsaved changes">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="3" /></svg>
                    </span>
                ) : null}
            </td>
        </tr>
    );
}
